/**
 * MatchEnd (DESIGN-client §7): the result, the score, and one obvious thing to do next.
 *
 * The moment comes first. A player who has just won wants to be told they won, in one word, before
 * they are asked to vote on anything — so VICTORY or DEFEAT in the team colour, then the score, then
 * a single primary action. Money Hand is a real option but a rare one, so it is a secondary line
 * with its terms spelled out rather than a button of equal weight next to Play again.
 *
 * Leave actually leaves. It used to cast a `leave` vote and sit there, which left the player on a
 * dead end screen watching a table they had already decided to walk away from.
 */
import { useEffect, useRef, useState } from 'react';
import { type ProgressMessage, crossedLevel, levelProgress, tierName } from '@spades/shared';
import { useStore } from '../../store/store';
import { SaveProgressCta } from '../sheets/Account';
import { seatDisplayName, teamLabel, teamOfSeat } from '../helpers';
import { intents, leaveToMenu } from '../runtime';
import { Avatar, Button, Panel } from '../ui';
import { LevelBadge } from '../ui/LevelBadge';
import { SiteLink } from '../ui/SiteLink';

/** Kept as a re-export: `leaveToMenu` now lives in `shell/runtime.ts`, shared with the lobby and Settings. */
export { leaveToMenu };

const VOTE_LABEL: Record<string, string> = { rematch: 'Run it back', money: 'Money Hand', leave: 'Leaving' };

/** How long the table is held open for votes before the room tears itself down. */
const REMATCH_WINDOW_MS = 60_000;

/** How long the XP bar takes to walk from the match's starting total to its new one. */
export const XP_WALK_MS = 900;

/**
 * Reduced motion, from the OS or from Settings — the same pair `theme.css` folds together for CSS.
 *
 * The walk below is JavaScript, so the stylesheet's blanket `animation-duration: 0.001ms` cannot
 * reach it. A player who asked for stillness is shown the final bar immediately instead.
 *
 * A near-copy of the hook in `HandEnd.tsx`, which is not exported and belongs to another agent's
 * file in this change; the two should be lifted into `shell/helpers.ts` once both have landed.
 */
function useReducedMotion(): boolean {
  const pref = useStore((s) => s.settings.motion);
  const [os, setOs] = useState(() => {
    try {
      return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    try {
      const mq = matchMedia('(prefers-reduced-motion: reduce)');
      const on = (e: MediaQueryListEvent): void => setOs(e.matches);
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    } catch {
      return;
    }
  }, []);
  if (pref === 'reduced') return true;
  if (pref === 'full') return false;
  return os;
}

/**
 * Walk `from` → `to` over `XP_WALK_MS` on rAF, easing out. Pinned to `to` on the last frame.
 *
 * The walk is RE-ARMED during render rather than only in the effect, because the end screen is
 * usually on screen BEFORE the XP is: the room sends `progress` after a database round trip, so
 * this hook's first render is `(0, 0)` and the real numbers arrive later. Left to the effect alone
 * the player would see one painted frame of "level 1, 0 XP" before the bar jumped to where they
 * actually are — a whole level wrong, on the one screen that is about levels.
 */
function useXpWalk(from: number, to: number, run: boolean): number {
  const [value, setValue] = useState(run ? from : to);
  const [range, setRange] = useState({ from, to });
  const frame = useRef(0);
  if (range.from !== from || range.to !== to) {
    setRange({ from, to });
    setValue(run ? from : to);
  }
  useEffect(() => {
    if (!run) {
      setValue(to);
      return;
    }
    const started = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - started) / XP_WALK_MS);
      // Cubic ease-out: fast off the mark, so the size of the gain reads before the bar settles.
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(t >= 1 ? to : Math.round(from + (to - from) * eased));
      if (t < 1) frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [from, to, run]);
  return value;
}

/** The ledger, in the order a player reads it: what the match paid, then what showing up paid. */
function ledgerRows(p: ProgressMessage): Array<{ label: string; xp: number }> {
  const rows: Array<{ label: string; xp: number }> = [{ label: 'Match', xp: p.base }];
  if (p.win > 0) rows.push({ label: 'Win', xp: p.win });
  if (p.nils > 0) rows.push({ label: p.nils > 50 ? 'Nils made' : 'Nil made', xp: p.nils });
  if (p.bonus > 0) rows.push({ label: p.bonusKind === 'first_game' ? 'First game' : 'Welcome back', xp: p.bonus });
  return rows;
}

/**
 * XP Panel — what the match just paid, and where it puts the player on the ladder.
 *
 * Deliberately not a ceremony. Ringmaster stopped the whole screen for a modal with a count-up and
 * a burst; here the result, the rematch vote and the clock all want the same moment, so this is a
 * panel under the totals that fills its bar once and then sits still.
 *
 * The bar is driven by a single walked scalar — lifetime XP — and every number on screen is derived
 * from it through `levelProgress`. That is what makes a level-up work with no special case: the
 * fill runs to the top of the old level, wraps to empty, and the badge's numeral flips, because
 * that is simply what the curve says about the XP the bar is holding at that instant.
 *
 * Renders nothing when there is no message. A table whose server has no database never shows an
 * empty panel promising XP that is not coming.
 */
export function XpPanel({ progress }: { progress: ProgressMessage | null }) {
  const reduced = useReducedMotion();
  const xp = useXpWalk(progress?.xpBefore ?? 0, progress?.xpAfter ?? 0, !reduced && !!progress);
  if (!progress) return null;

  const at = levelProgress(xp);
  // `crossedLevel` takes lifetime XP, not levels — it asks the curve, the same way the bar and the
  // badge's numeral do. Passing `levelBefore`/`levelAfter` typechecks and is silently always false
  // (levels 1 and 2 of XP are both level 1), which would have shipped a level-up line that never
  // once appeared. Reading it off the same function the bar is drawn from also means the sentence
  // and the badge can never disagree about whether a level happened.
  const leveled = crossedLevel(progress.xpBefore, progress.xpAfter);
  const rows = ledgerRows(progress);

  return (
    <Panel testId="xp-panel">
      <div className="xp">
        <div className="row row--between">
          <span className="eyebrow">Experience</span>
          <span className="xp__total tnum" data-testid="xp-total">
            +{progress.total}
          </span>
        </div>

        <div className="xp__ledger">
          {rows.map((r) => (
            <div key={r.label} className="xp__row">
              <span>{r.label}</span>
              <span className="tnum xp__row-xp">+{r.xp}</span>
            </div>
          ))}
        </div>

        <div className="xp__bar-row">
          <LevelBadge level={at.level} size={30} glow={leveled} />
          <div className="xp-bar">
            <span className="xp-bar__fill" style={{ transform: `scaleX(${at.fraction})` }} />
          </div>
          <span className="xp__numerals tnum">
            {at.intoLevel}/{at.required}
          </span>
        </div>

        {leveled ? (
          <p className="xp__levelup" data-testid="xp-levelup">
            Level up! — {tierName(progress.levelAfter)} {progress.levelAfter}
          </p>
        ) : (
          <p className="small muted xp__next" style={{ margin: 0 }}>
            {Math.max(0, at.required - at.intoLevel)} XP to level {at.nextLevel}
          </p>
        )}
      </div>
    </Panel>
  );
}

export function MatchEnd() {
  const result = useStore((s) => s.presentation.lastMatchResult);
  const table = useStore((s) => s.table);
  const seat = useStore((s) => s.session.seat);
  const me = useStore((s) => s.session.me);
  const platform = useStore((s) => s.session.platform);
  const lastProgress = useStore((s) => s.progress.lastMatch);

  const winner = (result?.winner ?? (table && table.winner >= 0 ? (table.winner as 0 | 1) : null)) as 0 | 1 | null;
  const totals: [number, number] = result?.totals ?? [table?.teams[0]?.score ?? 0, table?.teams[1]?.score ?? 0];
  const iWon = winner != null && seat != null && teamOfSeat(seat) === winner;
  const myVote = me && table ? table.rematchVotes[me.id] : undefined;
  const isHost = !!(me && table && table.hostPlayerId === me.id);

  // The rematch window is a real deadline: the room closes when it lapses, and a table where
  // nobody says anything should not sit open forever. Counting from mount is close enough — the
  // screen appears with the matchEnd cue, which is when the server starts its own clock.
  const [mountedAt] = useState(() => Date.now());
  const [now, setNow] = useState(mountedAt);
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(iv);
  }, []);
  const secondsLeft = Math.max(0, Math.ceil((mountedAt + REMATCH_WINDOW_MS - now) / 1000));

  const votes = (table?.seats ?? [])
    .map((s, i) => ({ s, i, vote: s.playerId ? table?.rematchVotes[s.playerId] : undefined }))
    .filter(({ s }) => s.controller === 'human' || s.controller === 'bot');

  return (
    <div className="screen screen--center screen--overlay screen--overlay-strong match-end" data-testid="screen-match-end">
      <div className="screen__inner">
        <div
          className={`banner${winner != null ? ` banner--team-${winner}` : ''}${
            seat != null && winner != null ? (iWon ? ' banner--victory' : ' banner--defeat') : ''
          }`}
          data-testid="winner-banner"
        >
          <div className="eyebrow" style={{ color: 'inherit', opacity: 0.8 }}>
            {result?.suddenDeath ? 'Sudden death' : result?.format?.kind === 'money' ? 'Money Hand' : 'Final'}
          </div>
          <h1 className="display display--xl stamp" data-testid="result-word">
            {winner == null ? 'Match over' : seat == null ? `${teamLabel(winner, null)} wins` : iWon ? 'VICTORY' : 'DEFEAT'}
          </h1>
        </div>
        {seat != null && (
          <div className="row row--between small muted" data-testid="rematch-window">
            <span>{secondsLeft > 0 ? 'Vote for what happens next' : 'Table closing…'}</span>
            <span className="tnum">{secondsLeft}s</span>
          </div>
        )}
        <Panel>
          <div className="row row--between">
            {([0, 1] as const).map((t) => (
              <div key={t} style={{ textAlign: 'center', flex: 1 }}>
                <div className="eyebrow">{teamLabel(t, seat)}</div>
                <div className="display display--lg tnum" data-testid={`final-${t}`}>
                  {totals[t]}
                </div>
              </div>
            ))}
          </div>
          {result && (
            <div className="small muted" style={{ textAlign: 'center', marginTop: 8 }}>
              {result.hands} hand{result.hands === 1 ? '' : 's'}
            </div>
          )}
        </Panel>
        {/* Under the totals, above the vote: the score is what happened, the XP is what it was
            worth, and both are read before anybody decides whether to run it back. */}
        <XpPanel progress={lastProgress} />
        {/* The moment a guest has the most to lose: they just watched the bar fill. Web guests only;
            renders nothing anywhere else. */}
        <SaveProgressCta where="match_end" level={lastProgress?.levelAfter} />
        {seat != null && (
          <div className="stack">
            {/* The two real actions share a wrapper so a short viewport can lay them side by side.
                A phone held sideways has 390px of height for a screen that wants 590, and stacking
                every control was most of the difference. Leave is a real button, not a ghost: it is
                the only way off this screen, and as bare text it read as a heading. */}
            <div className="match-end__buttons">
              <Button variant="primary" size="lg" block testId="vote-rematch" aria-pressed={myVote === 'rematch'} onClick={() => intents()?.rematchVote('rematch')}>
                Play again
              </Button>
              <Button block testId="vote-leave" onClick={leaveToMenu}>
                Leave
              </Button>
            </div>
            {/* Secondary, and explained. "Money Hand" means nothing to a player who has not met it,
                and as a button beside Play again it read as an equal choice rather than the rarer
                one it is. */}
            <p className="small" style={{ textAlign: 'center', margin: 0 }}>
              <button
                type="button"
                className="link"
                data-testid="vote-money"
                aria-pressed={myVote === 'money'}
                onClick={() => intents()?.rematchVote('money')}
              >
                Money Hand
              </button>{' '}
              <span className="muted">— one more hand, winner takes it.</span>
            </p>
            {/* `rematchVote` carries only rematch | money | leave, so a "Classic" vote has no wire
                to travel on. The honest equivalent: run it back, then change the format from the
                lobby's Table rules. Host-only, private tables only. */}
            <p className="small muted" style={{ textAlign: 'center', margin: 0 }} data-testid="leave-note">
              A bot takes your seat so the others can finish.
            </p>
            {isHost && (
              <p className="small muted" style={{ textAlign: 'center', margin: 0 }} data-testid="classic-hint">
                Want a long game? Play again, then switch to Classic under Table rules.
              </p>
            )}
          </div>
        )}
        {votes.length > 0 && (
          <div className="votes" data-testid="votes">
            {votes.map(({ s, i, vote }) => (
              <span
                key={i}
                className={`vote${vote ? ' vote--in' : ''}`}
                data-testid={`vote-seat-${i}`}
                title={`${seatDisplayName(s)}: ${vote ? VOTE_LABEL[vote] ?? vote : 'still deciding'}`}
              >
                <Avatar name={seatDisplayName(s)} src={s.avatar} size={24} team={teamOfSeat(i)} />
                {/* First name only: the avatar already carries identity, and full names turn a row
                    of four chips into three wrapped lines on a phone. */}
                {seatDisplayName(s).split(' ')[0]}
                {': '}
                {/* A bot never votes, so an ellipsis on its chip would read as "still waiting on
                    this seat" and make the table look stuck. Say what it actually is. */}
                <span className={vote ? '' : 'muted'}>
                  {s.controller === 'bot' ? 'Bot' : vote ? VOTE_LABEL[vote] ?? vote : '…'}
                </span>
              </span>
            ))}
          </div>
        )}
        {platform === 'web' && (
          <p className="small muted" style={{ textAlign: 'center', margin: 0 }}>
            {/* New tab: the rematch vote is still running behind this panel. */}
            <SiteLink className="link" path="/discord" newTab testId="bring-to-discord">
              Bring this table to your server
            </SiteLink>
          </p>
        )}
      </div>
    </div>
  );
}
